import {
  DatePickerUncontrolled,
  Table,
  DateRangeSelect,
} from "@4uhub/lib4uhub";
import { GridColDef } from "@mui/x-data-grid";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";

import useDate from "../../../hooks/useDate";
import { enUS, es, ptBR } from "date-fns/locale";
import MainApi from "../../../services/mainApi.service";
import ErrorMessage from "./ErrorMessage";
import Form from "./Form";
import { IEvaluationLog } from "./model";
import Ticket from "./Ticket";
import EvaluationId from "./EvaluationId";
import { Box, Stack } from "@mui/material";

const translationPath = "page.evaluation.logs.";

const ROUTE = process.env.REACT_APP_MAIN + "/api/v1/EvalMovEncSatisfaction";

const logRegisterErrorService = new MainApi<IEvaluationLog>(ROUTE);

const EvaluationLogs = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [enableFields, setEnableFields] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<Date | null>();

  const [startDate, setStartDate] = useState<Date | null>();
  const { localeFormat } = useDate();

  const columns: GridColDef<IEvaluationLog>[] = useMemo(
    () => [
      {
        field: "evaluationForm",
        headerName: t(translationPath + "evaluationForm") || "Form",
        renderCell: (params) => <Form data={params.value} />,
      },
      {
        field: "ticket",
        headerName: t(translationPath + "ticket") || "Ticket",
        renderCell: (params) => <Ticket data={params.value} />,
      },
      {
        field: "encounterIntegrationId",
        headerName:
          t(translationPath + "encounterIntegrationId") || "Encounter number",
      },
      {
        field: "attempts",
        headerName: t(translationPath + "attempts") || "Attempts",
      },
      {
        field: "created",
        headerName: t(translationPath + "shippingDate") || "Shipping Date",
        renderCell: (params) => {
          if (!params.value) return "-";

          const initialDate = new Date(params.value);
          return format(initialDate, "P pp", {
            locale:
              language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
          }).replaceAll(",", "");
        },
      },
      {
        field: "answerDate",
        headerName: t(translationPath + "answerDate") || "Answer Date",
        renderCell: (params) => {
          if (!params.value) return "-";

          const initialDate = new Date(params.value);
          return format(initialDate, "P pp", {
            locale:
              language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
          }).replaceAll(",", "");
        },
      },
      {
        field: "id",
        headerName: t(translationPath + "id") || "Id da Pesquisa",
        renderCell: (params) => (
          <EvaluationId
            data={params.value}
            portalDomain={params.row.portalDomain}
          />
        ),
      },
      {
        field: "errorMessage",
        headerName: t(translationPath + "errorMessage") || "Error Message",
        renderCell: (params) => <ErrorMessage>{params.value}</ErrorMessage>,
        maxWidth: 150,
      },
    ],
    [t, localeFormat]
  );

  const props = useMemo(() => {
    return {
      StartDate: startDate ? startDate : undefined,
      EndDate: endDate ? endDate : undefined,
    };
  }, [startDate, endDate]);

  return (
    <Table<IEvaluationLog>
      showDefaultMacroFilters={false}
      service={logRegisterErrorService}
      columns={columns}
      title={t(translationPath + "title")}
      searchInput={false}
      showButton={false}
      showDelete={false}
      serviceProps={props}
      showDeleteButton={false}
      defaultActions={false}
      macroFilters={() => {
        return (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: "0 0 200px" }}>
              <DateRangeSelect
                setEndDate={setEndDate}
                setStartDate={setStartDate}
                setEnableFields={setEnableFields}
              />
            </Box>

            <Box sx={{ flex: "0 0 150px" }}>
              <DatePickerUncontrolled
                value={startDate}
                onChange={setStartDate}
                label={`${t(translationPath + "start_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
            <Box sx={{ flex: "0 0 150px" }}>
              <DatePickerUncontrolled
                value={endDate}
                onChange={setEndDate}
                label={`${t(translationPath + "end_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
          </Stack>
        );
      }}
      hideLog
      searchInputPlaceHolder={t(translationPath + "placeholder")}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default memo(EvaluationLogs);
